import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { ProjectsService } from '../../services/projects.service';
import { RouterUrls } from '../../shared/router-urls';
import * as Projects from '../../shared/projects.model';
import { ReclaimVotesModalData } from '../../shared/projects.model';
import { ProjectDialogComponent } from '../project-dialog/project-dialog.component';
import { VoteDialogComponent } from '../vote-dialog/vote-dialog.component';
import { VoteCompleteDialogComponent } from '../vote-complete-dialog/vote-complete-dialog.component';
import { ReclaimVoteDialogComponent } from '../reclaim-vote-dialog/reclaim-vote-dialog.component';
import { VoteConfirmationDialogComponent } from '../vote-confirmation-dialog/vote-confirmation-dialog.component';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language.service';
import {
  SubscriptionDialogComponent
} from '../../your-impact-module/your-impact/subscription-dialog/subscription-dialog.component';
import { TagsService } from 'src/app/services/tags.service';
import { ProjectsConstants } from '../projects.constants';
import { Constants } from '../../app.constants';
import { ModalQueueService } from '../../shared/services/modal/modal-queue.service';

const ANIMATION_DURATION = 250;

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss']
})

export class ProjectComponent implements OnInit, OnDestroy {

  RouterUrls = RouterUrls;
  Constants = Constants;
  clickEventSubscription: Subscription;
  widthSubs: Subscription;
  widthDesktopSubs: Subscription;
  projectInfo: Projects.FullProject;
  alsoLikeProjects$: Observable<Projects.PartialProject[]>;
  isPledge: boolean;
  hasSubscription: boolean;
  isLogged = false;
  isVoted = false;
  isMobile = false;
  commonDialogConfig = {
    width: '737px',
    disableClose: true,
    panelClass: 'vote-dialog',
    maxWidth: '737px',
    closeOnNavigation: true,
    backdropClass: 'backdropBackground',
    data: {}
  };
  ownMoneyProjects: Projects.PartialProject[];
  isLoading = true;
  currentUrl: string;
  tab = '';
  backCustomUrl: string;

  isLoaded = false;

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private projectsService: ProjectsService,
              private authService: AuthService,
              private titleService: Title,
              private languageService: LanguageService,
              private tagsService: TagsService,
              private translate: TranslateService,
              private dialog: MatDialog,
              private modalQueue: ModalQueueService,
              private breakpointObserver: BreakpointObserver,
              private meta: Meta) {

    this.getNavigationExtras();
    activatedRoute.params
      .pipe(
        // Get project info and save to variable;
        switchMap(param => this.projectsService.getFullProjectInfo(param.slug)),
        tap(value => {
          this.projectInfo = value;
          this.isVoted = this.projectsService.isVoted(this.projectInfo.id);
          this.alsoLikeProjects$ = this.projectsService.getRelatedProjects(value.id);
          this.updateTags();
          this.isLoading = false;
        }),
        // Redirect to /browse-projects if there is no project with such id;
        catchError((error) => {
          this.router.navigate(['../'], {relativeTo: this.activatedRoute});
          return of(error);
        }),
        // Get user status (pledge and subscription) and save in variables;
        switchMap(() => this.authService.getCurrentUser()),
        tap(
          (user) => {
            this.isPledge = user.pledge_id !== null;
            this.hasSubscription = user.has_sub;
            this.isLogged = true;
            this.isLoaded = true;
          }, () => {
            this.isLogged = false;
            this.isLoaded = true;
          }
        ),
        // Get action when user click on action button in project dialog;
        switchMap(() => of(window.history.state.action)),
        tap(action => {
          if (action) {
            // Handle action and do proper method;
            this.handleActionFromProjectDialog(action);
          }
        })
      )
      .subscribe();

    this.projectsService.getSupportedProjects().subscribe();

    this.clickEventSubscription = this.tagsService.getClickEventProject().subscribe(() => {
      this.updateTags();
    });

    this.currentUrl = window.location.href;
    if (this.currentUrl.toLowerCase().endsWith('volunteering')) {
      this.tab = 'volunteering';
    }
  }

  get rootShare() {
    return RouterUrls.BrowseProjects;
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  ngOnInit(): void {
    this.widthSubs = this.breakpointObserver
      .observe(['(max-width: 767px)'])
      .subscribe((stateVar: BreakpointState) => {
        if (stateVar.matches) {
          this.projectsService.additionalMarginBottomFooter.next(120);
        } else {
          this.projectsService.additionalMarginBottomFooter.next(0);
        }
      });
    this.widthDesktopSubs = this.breakpointObserver
      .observe(['(max-width: 1100px)'])
      .subscribe((stateVar: BreakpointState) => {
        this.isMobile = stateVar.matches;
      });
    this.projectsService.getProjectsOwnMoney().subscribe(data => {
      if (data) {
        this.ownMoneyProjects = data;
      }
    });
  }

  updateTags() {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 1);
    const title = this.projectInfo?.name[this.currentLanguage].length > 64 ?
      this.projectInfo?.name[this.currentLanguage].slice(0, 64) : this.projectInfo?.name[this.currentLanguage];
    this.titleService.setTitle(title);
    let description = this.translate.instant('PREFERENCES_POPUP_1.' + this.projectInfo?.north_star + '_preferencespopup1') + ' | ';
    description += this.translate.instant('PREFERENCES_POPUP_2.' + this.projectInfo?.continent + '_preferencespopup2') + ' | ';
    description += this.projectInfo?.name[this.currentLanguage].length > 60 ? this.projectInfo?.name[this.currentLanguage].slice(0, 60) + '...' : this.projectInfo?.name[this.currentLanguage];
    description += ' | ' + this.translate.instant('PROJECT_PAGE.meta_description_projectpage');
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'URL' });
    this.meta.updateTag({ property: 'og:url', content: window.location.href });
    this.meta.updateTag({ property: 'og:image', content: this.projectInfo?.picture_cover });
  }

  handleActionFromProjectDialog(action: string) {
    switch (action) {
      case 'vote':
        this.onVoteButtonClick();
        break;
      case 'donate':
        this.onDonateButtonClick();
        break;
    }
  }

  getBackgroundImageWithGradient(pictureUrl: string) {
    return { backgroundImage: `linear-gradient(0deg, rgba(15,70,84,1) 0%, rgba(15,70,84,0) 45%, rgba(15,70,84,0) 100%), url(${ pictureUrl })` };
  }

  openProjectDialog(projectId: number) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '900px';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'project-dialog';
    dialogConfig.maxWidth = '900px';
    dialogConfig.closeOnNavigation = true;
    dialogConfig.data = projectId;
    dialogConfig.backdropClass = 'backdropBackground';

    const dialogRef = this.dialog.open(ProjectDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      data => {
        if (data) {
          this.openProjectDialog(data);
        }
      }
    );
  }

  onVoteButtonClick() {
    const dialogConfig: MatDialogConfig = { ...this.commonDialogConfig };
    dialogConfig.data.project = this.projectInfo;
    dialogConfig.data.isPledge = this.isPledge;

    if (!this.isPledge && !this.hasSubscription) {
      const subscriptiondialogConfig = new MatDialogConfig();
      subscriptiondialogConfig.width = '953px';
      subscriptiondialogConfig.disableClose = true;
      subscriptiondialogConfig.panelClass = 'subscription-dialog';
      subscriptiondialogConfig.maxWidth = '953px';
      subscriptiondialogConfig.closeOnNavigation = true;
      dialogConfig.backdropClass = 'backdropBackground';
      subscriptiondialogConfig.data = {
        oneTimeDonation: false,
        projectId: this.projectInfo.id
      };
      this.dialog.open(SubscriptionDialogComponent, subscriptiondialogConfig);
    } else {
      const dialogRef = this.dialog.open(VoteDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(
        data => {
          this.isVoted = this.projectsService.isVoted(this.projectInfo.id);
          if (data) {
            if (data === 'voteConfirmation') {
              this.openVoteConfirmatioDialog();
            }
            if (data === 'fiveVotesCompleted') {
              this.openVotesCompletedDialog();
            }
            if (data === 'reclaimVote') {
              this.openReclaimVoteDialog();
            }
          }
        }
      );
    }
  }

  onDonateButtonClick() {
    const subscriptiondialogConfig = new MatDialogConfig();
    subscriptiondialogConfig.width = '953px';
    subscriptiondialogConfig.disableClose = true;
    subscriptiondialogConfig.panelClass = 'subscription-dialog';
    subscriptiondialogConfig.maxWidth = '953px';
    subscriptiondialogConfig.closeOnNavigation = true;
    subscriptiondialogConfig.backdropClass = 'backdropBackground';
    subscriptiondialogConfig.data = {
      hasSub: this.hasSubscription,
      oneTimeDonation: false,
      projectId: this.projectInfo.id
    };
    if (this.hasSubscription) {
      subscriptiondialogConfig.data.oneTimeDonation = true;
    }
    this.dialog.open(SubscriptionDialogComponent, subscriptiondialogConfig);
  }

  onSignUpClick() {
    this.router.navigate([this.languageService.getCurrentLangWithSlash(), RouterUrls.SignUp]);
  }

  openReclaimVoteDialog() {
    const dialogConfig: MatDialogConfig = { ...this.commonDialogConfig };
    dialogConfig.data.projectId = this.projectInfo.id;
    const dialogRef = this.dialog.open(ReclaimVoteDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      (data: ReclaimVotesModalData) => {
        if (data.code === ProjectsConstants.RECLAIMED_VOTE) {
          this.openVotesCompletedDialog();
        }
      }
    );
  }

  openVoteConfirmatioDialog() {
    const dialogConfig: MatDialogConfig = {...this.commonDialogConfig};

    dialogConfig.data = {
      hasSubs: this.hasSubscription,
      isPledge: this.isPledge,
      projectId: this.projectInfo.id
    };

    this.modalQueue.addModalToQueue(
      {
        id: this.modalQueue.ids.VOTE_CONFIRMATION_ID,
        layer: 8,
        priority: 100,
        openModalFn: () => {
          const dialogRef = this.dialog.open(VoteConfirmationDialogComponent, dialogConfig);
          dialogRef.afterClosed().subscribe(() => {
            this.modalQueue.closeModal(this.modalQueue.ids.VOTE_CONFIRMATION_ID);
          })
        }
      }
    );
  }

  openVotesCompletedDialog() {
    const dialogConfig: MatDialogConfig = { ...this.commonDialogConfig };

    dialogConfig.data.project = this.projectInfo;
    dialogConfig.data = {
      project: this.projectInfo,
      hasSubs: this.hasSubscription,
    };

    const dialogRef = this.dialog.open(VoteCompleteDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      () => {
      }
    );
  }

  stopSupporting() {
    const dialogConfig: MatDialogConfig = { ...this.commonDialogConfig };

    dialogConfig.data = {
      project: this.projectInfo,
      stopSupporting: true,
    };

    const dialogRef = this.dialog.open(VoteDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      () => {
        this.isVoted = this.projectsService.isVoted(this.projectInfo.id);
      }
    );

  }

  isOwnMoney(projectId: number): boolean {
    let result = null;
    if (this.ownMoneyProjects) {
      result = this.ownMoneyProjects.find(project => project.id === projectId);
    }
    return result && result !== -1;
  }

  getSDGTitle(sdg: string) {
    return this.translate.instant('PROJECT_PAGE.' + sdg + '_title_projectpage');
  }

  getSDGText(sdg: string) {
    return this.translate.instant('PROJECT_PAGE.' + sdg + '_text_projectpage');
  }

  getVotedText() {
    return this.translate.instant('PROJECT_PAGE.voted_tooltip_projectpage');
  }

  getOwnMoneyText() {
    return this.translate.instant('PROJECT_PAGE.own_money_tooltip_projectpage');
  }

  ngOnDestroy(): void {
    this.projectsService.additionalMarginBottomFooter.next(0);
    this.widthSubs.unsubscribe();
    this.widthDesktopSubs.unsubscribe();
  }

  currentTab(url: string) {
    this.tab = url;
    window.scroll({top: 0});
  }

  /**
   *  Get navigation extras to perform custom actions (Example: Navigating back to some page)
   */
  getNavigationExtras() {
    const state = this.router.getCurrentNavigation()?.extras.state;
    switch (state?.[Constants.QPARAM_ACTION]) {
      case Constants.QPARAM_NAV_CONTEXT:
        if (state[Constants.QPARAM_NAV_CONTEXT] === Constants.NAV_CONTEXT_HOME) {
          this.backCustomUrl = Constants.NAV_CONTEXT_HOME;
        }
        break;
    }
  }

  onBackButtonClick() {
    switch (this.backCustomUrl) {
      case Constants.NAV_CONTEXT_HOME:
        this.router.navigate([this.languageService.getCurrentLangWithSlash(), RouterUrls.Home]);
        break;
      default:
        this.router.navigate([this.languageService.getCurrentLangWithSlash(), RouterUrls.BrowseProjects]);
        break;
    }

  }
}
